import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnInit,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { TranslateModule } from '@ngx-translate/core';

import { QueryFieldComponent } from '../../../shared/ui/query-field/query-field.component';
import { ChatMessageComponent } from '../../../shared/ui/chat-message/chat-message.component';
import { ChatbotExampleChipsComponent } from '../ui/chatbot-example-chips/chatbot-example-chips.component';
import { SourceSelectorComponent } from '../ui/source-selector/source-selector.component';
import { StateService } from '../../../core/data-access/state.service';
import { LoaderService } from 'src/app/core/data-access/loader.service';
import { ChatbotService } from '../data-access/chatbot.service';
import { Source } from '../../../shared/model/query-body.model';

@Component({
  selector: 'app-chatbot-feature',
  standalone: true,
  imports: [
    CommonModule,
    QueryFieldComponent,
    MatProgressSpinnerModule,
    ChatMessageComponent,
    MatButtonModule,
    TranslateModule,
    MatIconModule,
    ChatbotExampleChipsComponent,
    MatDividerModule,
    SourceSelectorComponent
  ],
  templateUrl: './chatbot-feature.component.html',
  styleUrls: ['./chatbot-feature.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatbotFeatureComponent implements OnInit, AfterViewChecked {
  @ViewChild('chatContainer') chatContainer: ElementRef;

  @Input() selectedFileNames: string[] | undefined;
  @Input() classes: string | undefined;

  chatbotService = inject(ChatbotService);
  destroyRef = inject(DestroyRef);
  private stateService = inject(StateService);
  private loaderService = inject(LoaderService);

  chat$ = this.stateService.chat$;
  exampleQuestion$ = this.stateService.exampleQuestion$;
  isLoading$ = this.loaderService.isLoading$;
  taskIds$ = this.stateService.getTaskIdsStream();
  selectedSource$ = this.stateService.selectedSource$;

  taskIds: string[] = [];

  isAccordionOpening = false;

  protected readonly Source = Source;

  ngOnInit() {
    if (this.selectedFileNames?.length) {
      this.stateService.setEmptyChatHistory();
    } else {
      this.stateService.loadChatHistory();
    }

    this.taskIds$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(ids => {
      this.taskIds = ids;
    });
  }

  ngAfterViewChecked() {
    this.scrollToBottomIfNotOpeningAccordion();
  }

  private scrollToBottomIfNotOpeningAccordion() {
    if (!this.isAccordionOpening) {
      setTimeout(() => {
        this.chatContainer.nativeElement.scrollTop =
          this.chatContainer.nativeElement.scrollHeight;
      }, 0);
    }
  }

  onSendMessage(message: string) {
    this.stateService.chat(message, this.selectedFileNames, this.taskIds);
    this.isAccordionOpening = false;
  }

  onSourceChange(source: Source) {
    this.stateService.updateSelectedSource(source);
  }

  clearHistory(): void {
    this.stateService.clearChatHistory();
  }

  onScrollPositionChange(y: number) {
    this.isAccordionOpening = true;
    this.chatContainer.nativeElement.scrollTop = y;
  }
}
